/* App Tư vấn cơ sở — lượt tư vấn lưu tạm trên máy (localStorage), chờ nối API */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import dayjs from 'dayjs';
import { BRANCHES } from '../Telesales/dashboard/data';
import { MIN, SEED_VER, seedVisits, type LogItem, type Screening, type StaffSt, type Visit } from './logic';

/** Lượt mẫu chưa ai thao tác cũ hơn chừng này phút thì làm mới theo giờ hiện tại */
const SEED_REFRESH_MIN = 60;
/** Dấu nhận biết lượt mẫu còn nguyên (chưa ai thao tác) */
const sigOf = (v: Visit) => { const s = JSON.stringify(v); let h = 5381; for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) | 0; return h; };
const sigsOf = (vs: Visit[]) => Object.fromEntries(vs.map((v) => [v.id, sigOf(v)]));

/** Vai trò đang xem: bác sĩ chỉ thấy bước Bác sĩ khám, không thấy tiền (giả lập — chờ phân quyền thật) */
export type ViewRole = 'consultant' | 'doctor';

interface TuVanStore {
  visits: Visit[];
  seedDate?: string;
  /** Lúc tạo / làm mới dữ liệu mẫu và dấu từng lượt mẫu lúc đó */
  seedAt?: number;
  seedSigs?: Record<string, number>;
  branchId: string;
  /** Tư vấn viên đang dùng máy (ghi vào lịch sử, nhận khách) */
  me?: string;
  staffSt: Record<string, StaffSt>;
  /** Lượt đang mở gần nhất — các menu sau mở tiếp lượt này */
  cur?: string;
  /** Chia khách tự động — tắt mặc định: buổi họp 30/10 khách muốn Lễ tân chia tay trước */
  autoAssign: boolean;
  role: ViewRole;
  /** Bác sĩ đang dùng máy khi xem với vai trò bác sĩ */
  meDoctor?: string;
  /** Hiện "Hành trình trước khi đến" ở Phiếu tư vấn */
  showJourney: boolean;
  /**
   * Phiếu kiosk đã lưu trên máy trước khi nối API, theo mã lịch hẹn; chỉ còn đọc lại, phiếu mới nằm trên server
   */
  screenings: Record<string, Screening>;
  set: (p: Partial<Pick<TuVanStore, 'branchId' | 'me' | 'cur' | 'autoAssign' | 'role' | 'meDoctor' | 'showJourney'>>) => void;
  setStaffSt: (id: string, st: StaffSt) => void;
  /** Mỗi ngày thay dữ liệu mẫu của ngày cũ bằng dữ liệu mẫu mới, giữ lượt do người dùng tạo */
  ensureSeed: (now: number) => void;
  reseed: (now: number) => void;
  add: (v: Visit[]) => void;
  /** Sửa một lượt; kèm dòng lịch sử nếu có */
  update: (id: string, fn: (v: Visit) => Partial<Visit>, log?: Omit<LogItem, 'at'> & { /** Việc đã xảy ra trước đó: chèn đúng vị trí theo giờ */ at?: number }) => void;
}

export const useTuVan = create<TuVanStore>()(
  persist(
    (set, get) => ({
      visits: [],
      branchId: 'q1',
      staffSt: {},
      autoAssign: false,
      role: 'consultant',
      showJourney: true,
      screenings: {},
      set: (p) => set(p),
      setStaffSt: (id, st) => set((s) => ({ staffSt: { ...s.staffSt, [id]: st } })),
      ensureSeed: (now) => {
        const day = `${dayjs(now).format('YYYY-MM-DD')}#${SEED_VER}`;
        // Lượt / cơ sở đang chọn thuộc cơ sở đã bỏ thì chuyển về cơ sở đầu danh sách
        const known = (id: string) => BRANCHES.some((b) => b.id === id);
        const home = (v: Visit) => (known(v.branchId) ? v : { ...v, branchId: BRANCHES[0].id });
        const s0 = get();
        if (s0.seedDate === day) {
          if (!known(s0.branchId) || s0.visits.some((v) => !known(v.branchId))) {
            set((s) => ({ branchId: known(s.branchId) ? s.branchId : BRANCHES[0].id, visits: s.visits.filter((v) => !v.seed || known(v.branchId)).map(home) }));
          }
          // Mẫu tạo trước bản này chưa có mốc: lấy trạng thái hiện tại làm mốc
          if (!s0.seedAt) { set({ seedAt: now, seedSigs: sigsOf(s0.visits.filter((v) => v.seed)) }); return; }
          // Mẫu tạo lúc sáng sớm thì giờ chờ, giờ check-in cũ dần: lượt mẫu chưa ai thao tác được làm mới theo giờ hiện tại
          if (now - s0.seedAt > SEED_REFRESH_MIN * MIN) {
            const fresh = new Map(seedVisits(now).map((v) => [v.id, v]));
            const sigs = s0.seedSigs ?? {};
            set((s) => {
              const visits = s.visits.map((v) => (v.seed && sigs[v.id] === sigOf(v) ? fresh.get(v.id) ?? v : v));
              return { visits, seedAt: now, seedSigs: Object.fromEntries(visits.filter((v) => v.seed).map((v) => [v.id, fresh.get(v.id) === v ? sigOf(v) : sigs[v.id]])) };
            });
          }
          return;
        }
        const seed = seedVisits(now);
        set((s) => ({
          seedDate: day,
          seedAt: now,
          seedSigs: sigsOf(seed),
          branchId: known(s.branchId) ? s.branchId : BRANCHES[0].id,
          visits: [...seed, ...s.visits.filter((v) => !v.seed).map(home)],
        }));
      },
      reseed: (now) => {
        const seed = seedVisits(now);
        set({ seedDate: `${dayjs(now).format('YYYY-MM-DD')}#${SEED_VER}`, seedAt: now, seedSigs: sigsOf(seed), visits: seed, cur: undefined });
      },
      add: (v) => set((s) => ({ visits: [...v, ...s.visits] })),
      update: (id, fn, log) => set((s) => ({
        visits: s.visits.map((v) => (v.id === id
          ? { ...v, ...fn(v), log: log ? (log.at ? [{ ...log, at: log.at }, ...v.log].sort((a, b) => b.at - a.at) : [{ ...log, at: Date.now() }, ...v.log]) : v.log }
          : v)),
      })),
    }),
    {
      name: 'khothi-app-tuvan-v1',
      version: 2,
      // v2: chia tự động tắt mặc định (Lễ tân chia tay trước), thêm vai trò xem và khai báo ban đầu
      migrate: (s: any, v) => (v < 2 ? { ...s, autoAssign: false, role: 'consultant', showJourney: true, screenings: {} } : s),
    },
  ),
);
